/**
 * ForceLogoutPanel — admin-only "kill switch" for all active sessions.
 *
 * Clicking the button POSTs /api/admin/force-logout-all which sets a
 * server-side `force_logout_epoch` — every currently-issued JWT (whose
 * `iat` < epoch) is immediately rejected with a 401 that includes
 * `code: "session_invalidated"`. The frontend's axios interceptor then
 * wipes local storage / service workers / cache and hard-redirects the
 * offending user to /login.
 *
 * Because the caller's OWN token is also invalidated, the button
 * confirms this consequence loudly before firing.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, LogOut, Loader2, Check } from 'lucide-react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const ForceLogoutPanel = () => {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/force-logout-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data);
    } catch {
      setStatus({ epoch: 0, triggered_at: null });
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleForceLogout = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/admin/force-logout-all`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          _noRetry: true,
        },
      );
      toast.success('All sessions invalidated', {
        description:
          "Every user is being logged out. You'll be redirected to log in with a fresh session in a moment.",
        duration: 5000,
      });
      setStatus({
        epoch: res.data.epoch,
        triggered_at: new Date().toISOString(),
      });
      // Do NOT navigate here — the next API call this component (or any
      // other) makes will return 401 session_invalidated, and the global
      // axios interceptor handles logout + cache-bust + redirect. That
      // keeps the "you got kicked out too" UX identical for admins and
      // regular users.
    } catch (err) {
      toast.error('Force logout failed', {
        description: err?.response?.data?.detail || 'Please try again.',
      });
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };

  const lastTriggered = status?.triggered_at
    ? new Date(status.triggered_at).toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : null;

  return (
    <Card
      className="bg-gray-900 border border-red-500/25"
      data-testid="force-logout-panel"
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white">
          <LogOut className="h-5 w-5 text-red-400" />
          Force Logout All Users
        </CardTitle>
        <CardDescription className="text-gray-400">
          Invalidate every active session — including your own — so every user
          must log in again. Use this after a security event, an emergency
          admin credential rotation, or when pushing an update that must be
          picked up immediately by clients.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/25 text-amber-100 text-sm">
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5 text-amber-400" />
          <div>
            <div className="font-medium">This action logs YOU out too.</div>
            <div className="text-amber-200/80 text-xs mt-1">
              Every user with an active session (including this browser tab)
              will be redirected to the login screen on their next click. Their
              browser cache will also be cleared so the freshly logged-in
              session loads the latest deployed build.
            </div>
          </div>
        </div>

        {loadingStatus ? (
          <div className="text-xs text-gray-500 flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" /> Checking last invalidation…
          </div>
        ) : lastTriggered ? (
          <div className="text-xs text-gray-500 flex items-center gap-2">
            <Check className="h-3 w-3 text-emerald-500" />
            Last triggered: <span className="text-gray-300">{lastTriggered}</span>
          </div>
        ) : (
          <div className="text-xs text-gray-500">
            No force-logout has ever been triggered on this environment.
          </div>
        )}

        {!confirmOpen ? (
          <Button
            variant="destructive"
            onClick={() => setConfirmOpen(true)}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="force-logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Force Logout All Users
          </Button>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-white font-medium">
              Are you sure? This cannot be undone.
            </span>
            <Button
              variant="destructive"
              onClick={handleForceLogout}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="force-logout-confirm"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Logging out everyone…
                </>
              ) : (
                <>
                  <LogOut className="h-4 w-4 mr-2" />
                  Yes, log out all users
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              disabled={submitting}
              className="text-gray-300 hover:bg-white/5"
              data-testid="force-logout-cancel"
            >
              Cancel
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ForceLogoutPanel;
